import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import type { ITheme } from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { Text } from '@inter-ikea-kompis/component-text';

import type { ButtonVariant } from '@ingka/button-webc';
import {
	Button as SkapaButton,
	IconButton as SkapaIconButton
} from '@ingka/button-webc/passive.js';
import { arrowLeft, cross, registerIcon } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';

import SheetHeaderStyle from './SheetHeaderStyle.js';

/**
 * SheetHeader component.
 *
 * @event closeButtonClick Triggered when the user clicks on the back button icon.
 * @event backButtonClick When next button is clicked.
 */

export default class SheetHeader extends KompisElement {
	// Heading
	@property({ type: String })
	public heading: string | null = null;

	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Z-index.
	@property({ type: Number })
	public zIndex = 501;

	// Floating header.
	@property({ type: Boolean })
	public floatingHeader = false;

	// Back icon as default.
	@property({ type: Boolean })
	public backIcon = false;

	// Label for the close icon button to set as an aria-label for accessibility reasons. There is a common translation modalAriaLabelButtonClose you can use.
	@property({ type: String })
	public ariaLabelCloseButton: string | null = null;

	// Label for the back icon button to set as an aria-label for accessibility reasons. There is a common translation modalAriaLabelButtonBack you can use.
	@property({ type: String })
	public ariaLabelBackButton: string | null = null;

	/**
	 * Renders the component.
	 *
	 * @returns Template.
	 */
	public override render(): RenderResult {
		return html`
			<style>
				${SheetHeaderStyle.getStyle({
					theme: this.theme,
					zIndex: this.zIndex,
					floatingHeader: this.floatingHeader
				})}
			</style>
			<div class="headingContainer">
				${this.getBackButton()}
				<h2 class="header">
					<kompis-text .theme="${this.theme}" .type="${ThemeFontStyleTypeEnum.headingSmall}"
						>${this.heading}</kompis-text
					>
				</h2>
				${this.getCloseButton()}
			</div>
		`;
	}

	public override async connectedCallback(): Promise<void> {
		registerIcon(arrowLeft);
		registerIcon(cross);

		this.customElements.define('skapa-button', SkapaButton);
		this.customElements.define('skapa-icon-button', SkapaIconButton);
		this.customElements.define('skapa-icon', SkapaIcon);
		this.customElements.define('kompis-text', Text);

		super.connectedCallback();
	}
	/**
	 * @returns Close button.
	 */
	private getCloseButton(): RenderResult {
		const buttonVariant: ButtonVariant = this.floatingHeader ? 'primary' : 'tertiary';

		return html`
			<skapa-icon-button
				class="crossIcon"
				variant="${buttonVariant}"
				?inverse="${this.floatingHeader ? true : false}"
				size="small"
				@click="${this.onCloseClick.bind(this)}"
			>
				<skapa-icon slot="icon" icon="cross"></skapa-icon>
				${this.ariaLabelCloseButton || ''}
			</skapa-icon-button>
		`;
	}

	/**
	 * On close click event.
	 */
	private onCloseClick(): void {
		this.dispatchEvent(
			new CustomEvent('closeButtonClick', { bubbles: true, detail: { type: 'button' } })
		);
	}

	/**
	 * @returns Back button.
	 */
	private getBackButton(): RenderResult {
		if (!this.backIcon) {
			return html`<div class="filler"></div>`;
		}

		const buttonVariant: ButtonVariant = this.floatingHeader ? 'primary' : 'tertiary';

		return html`
			<skapa-icon-button
				class="backIcon"
				variant="${buttonVariant}"
				?inverse="${this.floatingHeader ? true : false}"
				size="small"
				@click="${this.onBackClick.bind(this)}"
			>
				<skapa-icon flip-rtl slot="icon" icon="arrow-left"></skapa-icon>
				${this.ariaLabelBackButton || ''}
			</skapa-icon-button>
		`;
	}

	/**
	 * On back click event.
	 */
	private onBackClick(): void {
		this.dispatchEvent(
			new CustomEvent('backButtonClick', { bubbles: true, detail: { type: 'button' } })
		);
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'skapa-button': SkapaButton;
		'skapa-icon-button': SkapaIconButton;
		'skapa-icon': SkapaIcon;
		'kompis-sheet-header': SheetHeader;
	}
}
